/**
 * InlineMediaButton — bouton "Modifier ce média" visible en mode admin.
 *
 * Utilisé pour remplacer une vidéo/image/audio hero sur une page immersive.
 * Cliquer ouvre le MediaPicker → save le filename dans le CMS via onSave.
 *
 * Props :
 *   label      — texte du bouton ("Modifier la vidéo hero" par ex.)
 *   accept     — filtre accept du picker (par défaut "image/*,video/*")
 *   onPick     — callback(filename) — reçoit le basename choisi ou uploadé
 *   position   — "tl" | "tr" | "bl" | "br" | "bc" (par défaut "tl")
 *   className  — classes additionnelles
 *   currentValue — valeur actuelle (affichée dans un badge en dessous)
 *   allowClear — si true, ajoute un bouton "×" pour vider (setter null)
 *
 * Rendu :
 *   - Anonyme (non admin) : ne rend rien.
 *   - Admin : bouton flottant + modal MediaPicker à la demande.
 */

import { useEffect, useState } from "react";
import { Image as ImageIcon, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import MediaPicker from "./MediaPicker";

const POS = {
  tl: "top-20 left-3",
  tr: "top-20 right-3",
  bl: "bottom-3 left-3",
  br: "bottom-3 right-3",
  bc: "bottom-3 left-1/2 -translate-x-1/2",
  tc: "top-20 left-1/2 -translate-x-1/2",
};

export default function InlineMediaButton({
  label = "Modifier",
  accept = "image/*,video/*",
  onPick,
  position = "tl",
  className = "",
  currentValue = "",
  allowClear = false,
  onClear,
  site = "refuge",
  dataTestid,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const isAdmin = !!user;

  useEffect(() => {
    if (open) api.listMedia(site).then(setUploaded).catch(() => {});
  }, [open, site]);

  if (!isAdmin) return null;

  return (
    <>
      <div
        className={`absolute ${POS[position] || POS.tl} z-30 flex flex-col gap-1.5 items-stretch ${className}`}
        data-testid={dataTestid || "inline-media-button"}
      >
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-refuge-ruby text-white text-[11px] font-body uppercase tracking-[0.15em] shadow-lg hover:bg-refuge-ruby/90 transition-all backdrop-blur-sm"
            title={label}
          >
            <ImageIcon size={12} strokeWidth={1.5} />
            {label}
          </button>
          {allowClear && currentValue && (
            <button
              type="button"
              onClick={() => onClear && onClear()}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/60 text-white shadow-lg hover:bg-black/80 transition-all"
              title="Retirer ce média"
            >
              <X size={12} strokeWidth={1.5} />
            </button>
          )}
        </div>
        {currentValue && (
          <div className="text-[9px] font-mono text-white/85 bg-black/50 px-2 py-1 rounded-md truncate max-w-[240px] backdrop-blur-sm">
            {currentValue}
          </div>
        )}
      </div>
      {open && (
        <MediaPicker
          uploaded={uploaded}
          onPick={(filename) => { onPick && onPick(filename); setOpen(false); }}
          onClose={() => setOpen(false)}
          onUploaded={async () => {
            const l = await api.listMedia(site);
            setUploaded(l);
          }}
          site={site}
          accept={accept}
        />
      )}
    </>
  );
}
